import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, Card, CardContent, CardActions, Dialog, IconButton, TextField, InputAdornment, Avatar, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, useMediaQuery, Paper, Radio, RadioGroup, FormControlLabel, FormHelperText } from '@mui/material';
import { Edit, Close, Phone } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { deleteAdminAction, getAllAdminsAction, updateAdminAction, updateSubscriptionAction } from '../../store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';

function SuperAdminSubscriptionManagement() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [phoneSearch, setPhoneSearch] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [subscriptionFilter, setSubscriptionFilter] = useState('expired'); // Default to expired
  const [formErrors, setFormErrors] = useState({});

  const { successData } = useSelector(state => state.postCreateAdminReducer);
  const { successData: updatedsubscriptionSuccessData } = useSelector(state => state.updateSubscriptionReducer);

  // Form state for editing user
  const [editForm, setEditForm] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    subscription_plan: '',
    subscription_status: ''
  });

  useEffect(() => {
    // Filter users based on subscription status and phone search
    let filtered = users;

    // Apply subscription filter
    if (subscriptionFilter !== 'all') {
      filtered = filtered.filter(user => user.subscription === subscriptionFilter);
    }

    // Apply phone search
    if (phoneSearch.trim() !== '') {
      filtered = filtered.filter(user =>
        user.phoneNumber.includes(phoneSearch)
      );
    }

    setFilteredUsers(filtered);
  }, [phoneSearch, users, subscriptionFilter]);

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      fullName: user.fullName,
      email: user.email,
      phoneNumber: user.phoneNumber,
      subscription_plan: user.subscription_plan,
      subscription_id: user.subscription_id,
      subscription_status: user.subscription
    });
    setFormErrors({});
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingUser(null);
    setFormErrors({});
  };

  const handleFormSubmit = (userData) => {
    if (editingUser) {
      const updatedUsers = users.map(user => user.id === editingUser.id ? { ...user, ...userData } : user);
      setUsers(updatedUsers);
      toast.success('User updated successfully');
    }
    handleCloseEditDialog();
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Add this function to handle subscription status change
  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setEditForm(prev => ({
      ...prev,
      subscription_status: newStatus,
      // If status is expired, clear the subscription plan
      subscription_plan: newStatus === 'expired' ? '' : prev.subscription_plan
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!editForm.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    if (!editForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(editForm.email)) {
      errors.email = 'Email is invalid';
    }
    if (!editForm.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required';
    }
    if (!editForm.subscription_status) {
      errors.subscription_status = 'Subscription status is required';
    }
    if (editForm.subscription_status === 'active' && !editForm.subscription_plan) {
      errors.subscription_plan = 'Subscription plan is required for active subscriptions';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditFormSubmit = () => {
    if (!validateForm()) {
      return;
    }
    dispatch(updateSubscriptionAction({
      endPoint: `super-admin/update-client-subscription/${editingUser.subscription_id}`,
      full_name: editForm.fullName,
      email: editForm.email,
      mobile_no: editForm.phoneNumber,
      subscription_plan: editForm.subscription_plan ? editForm.subscription_plan : 'none',
      subscription_status: editForm.subscription_status, // Add this line
    }));
    handleCloseEditDialog();
  };

  const handleRemoveUser = (userId) => {
    setUserToDelete(userId);
    setConfirmDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    setRemovingUserId(userToDelete);
    try {
      const response = await dispatch(deleteAdminAction({
        endPoint: `super-admin/admin/${userToDelete}`,
      })).unwrap();

      if (response.success) {
        const updatedUsers = users.filter(user => user.id !== userToDelete);
        setUsers(updatedUsers);
        toast.success('User removed successfully');
        dispatch(getAllAdminsAction());
      } else {
        toast.error('Failed to remove user');
      }
    } catch (error) {
      toast.error('An error occurred while removing the user');
    } finally {
      setRemovingUserId(null);
      setConfirmDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const cancelDeleteUser = () => {
    setConfirmDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleSaveChangesClick = () => {
    if (validateForm()) {
      setOpenConfirmDialog(true);
    }
  };

  const handleConfirmSave = () => {
    setOpenConfirmDialog(false);
    handleEditFormSubmit();
  };

  const handleCancelSave = () => {
    setOpenConfirmDialog(false);
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  useEffect(() => {
    const fetchAdmins = async () => {
      setLoading(true);
      try {
        const response = await dispatch(getAllAdminsAction()).unwrap();
        if (response.success) {
          const admins = response.data.map(admin => ({
            subscription_id: admin.Client.id,
            subscription_status: admin.Client.subscription_status,
            subscription: admin.Client.subscription_status,
            subscription_plan: admin.Client.subscription_plan,
            id: admin.user_id,
            fullName: admin.full_name,
            email: admin.email,
            phoneNumber: admin.mobile_no,
          }));
          setUsers(admins);

          // Apply initial filter (default to expired)
          const initialFiltered = admins.filter(admin => admin.subscription === subscriptionFilter);
          setFilteredUsers(initialFiltered);

          console.log("All users:", admins);
        } else {
          toast.error('Failed to fetch admins');
        }
      } catch (error) {
        toast.error('An error occurred while fetching admins');
        console.error("Error fetching admins:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdmins();
  }, [dispatch, subscriptionFilter, updatedsubscriptionSuccessData]);

  useEffect(() => {
    if (successData) {
      window.location.reload();
    }
  }, [successData]);

  return (
    <Box sx={{ p: 3, mt: { xs: 12, sm: 6, md: 15 }, width: '75%', ml: { xs: '7%', sm: '20%' } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'space-between' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          mb: 3,
        }}>
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, marginBottom: { xs: 2, sm: 0 } }}>
          Subscription Management
        </Typography>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <TextField
          placeholder="Search by phone number..."
          variant="outlined"
          size="small"
          value={phoneSearch}
          onChange={(e) => setPhoneSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Phone />
              </InputAdornment>
            ),
            endAdornment: phoneSearch && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setPhoneSearch('')}>
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: { borderRadius: '8px' }
          }}
          sx={{ width: { xs: '100%', sm: '50%', md: '30%' } }}
        />

        <FormControl size="small" sx={{ width: { xs: '100%', sm: '30%', md: '20%' } }}>
          <InputLabel id="subscription-filter-label">Subscription Status</InputLabel>
          <Select
            labelId="subscription-filter-label"
            id="subscription-filter"
            value={subscriptionFilter}
            label="Subscription Status"
            onChange={(e) => setSubscriptionFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Typography align="center">Loading...</Typography>
      ) : filteredUsers.length === 0 ? (
        <Typography align="center">No users found with the selected filters</Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredUsers.map((user) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 3,
                borderRadius: '12px',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-5px)' },
                  boxShadow: 6,
                }
              }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'primary.main',
                        mb: 1
                      }}
                    >
                      {getInitials(user.fullName)}
                    </Avatar>
                    <Typography variant="h6" component="div" align="center">
                      {user.fullName}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <strong>Email:</strong>&nbsp;{user.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <strong>Phone:</strong>&nbsp;{user.phoneNumber}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={user.subscription === "expired" ? "error" : "success.main"}
                      sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                    >
                      <strong>Subscription:</strong>&nbsp;{user.subscription}
                    </Typography>
                    {user.subscription_plan && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                        <strong>Plan:</strong>&nbsp;{user.subscription_plan}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => handleEditUser(user)}
                    size="small"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleRemoveUser(user.id)}
                    size="small"
                    sx={{ ml: 1 }}
                  >Remove
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit User Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit User
          <IconButton
            aria-label="close"
            onClick={handleCloseEditDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" sx={{ mt: 1 }}>
            {/* Subscription Status */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Paper elevation={0} sx={{
                p: 2,
                backgroundColor: 'rgba(0, 123, 255, 0.05)',
                borderRadius: '12px',
                border: '1px solid rgba(0, 123, 255, 0.2)',
                mb: 2
              }}>
                <Typography variant="subtitle1" sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  color: '#007bff'
                }}>
                  Subscription Status
                </Typography>
                <RadioGroup
                  name="subscription_status"
                  value={editForm.subscription_status}
                  onChange={handleStatusChange}
                  row
                  sx={{ justifyContent: 'space-around' }}
                >
                  <FormControlLabel
                    value="active"
                    control={
                      <Radio
                        color="success"
                        sx={{
                          '&.Mui-checked': { color: '#28a745' },
                          transform: 'scale(1.1)'
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 500 }}>
                        Active
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="expired"
                    control={
                      <Radio
                        color="error"
                        sx={{
                          '&.Mui-checked': { color: '#dc3545' },
                          transform: 'scale(1.1)'
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 500 }}>
                        Expired
                      </Typography>
                    }
                  />
                </RadioGroup>
                {formErrors.subscription_status && (
                  <FormHelperText error sx={{ textAlign: 'center', mt: 1 }}>
                    {formErrors.subscription_status}
                  </FormHelperText>
                )}
              </Paper>
            </Grid>

            {/* Subscription Type */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Paper elevation={0} sx={{
                p: 2,
                backgroundColor: editForm.subscription_status === 'expired'
                  ? 'rgba(0, 0, 0, 0.05)'
                  : 'rgba(0, 123, 255, 0.05)',
                borderRadius: '12px',
                border: '1px solid rgba(0, 123, 255, 0.2)'
              }}>
                <Typography variant="subtitle1" sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  color: editForm.subscription_status === 'expired' ? 'text.disabled' : '#007bff'
                }}>
                  Subscription Type
                </Typography>
                <RadioGroup
                  name="subscription_plan"
                  value={editForm.subscription_plan}
                  onChange={handleEditFormChange}
                  row
                  sx={{ justifyContent: 'space-around' }}
                  disabled={editForm.subscription_status === 'expired'}
                >
                  <FormControlLabel
                    value="monthly"
                    disabled={editForm.subscription_status === 'expired'}
                    control={
                      <Radio
                        color="primary"
                        sx={{
                          '&.Mui-checked': { color: '#007bff' },
                          transform: 'scale(1.1)'
                        }}
                      />
                    }
                    label={
                      <Typography sx={{
                        fontWeight: 500,
                        color: editForm.subscription_status === 'expired' ? 'text.disabled' : 'inherit'
                      }}>
                        Monthly Subscription
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="annual"
                    disabled={editForm.subscription_status === 'expired'}
                    control={
                      <Radio
                        color="primary"
                        sx={{
                          '&.Mui-checked': { color: '#007bff' },
                          transform: 'scale(1.1)'
                        }}
                      />
                    }
                    label={
                      <Typography sx={{
                        fontWeight: 500,
                        color: editForm.subscription_status === 'expired' ? 'text.disabled' : 'inherit'
                      }}>
                        Yearly Subscription
                      </Typography>
                    }
                  />
                </RadioGroup>
                {formErrors.subscription_plan && (
                  <FormHelperText error sx={{ textAlign: 'center', mt: 1 }}>
                    {formErrors.subscription_plan}
                  </FormHelperText>
                )}
              </Paper>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveChangesClick}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelSave}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Save</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to save the changes?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSave}>No</Button>
          <Button
            variant="contained"
            onClick={handleConfirmSave}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteDialogOpen}
        onClose={cancelDeleteUser}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this user?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteUser}>No</Button>
          <Button
            variant="contained"
            onClick={confirmDeleteUser}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SuperAdminSubscriptionManagement;

